import { Button, Icon } from "armstrong-react";
import classNames from "classnames";
import React from "react";

import { isClickInside } from "../../../../packages/ui/src/utils/uiHelpers";
import { IconButton } from "./atoms";

import "./dropdownButton.scss";

interface Props {
  /**
   * Element to render inside the dropdown container.
   * This is a func prop rather than this.props.children to avoid rendering until the dropdown is opened.
   */
  renderDropdown: (onClose?: () => void) => JSX.Element;

  icomoon?: string;
  label?: string;
  className?: string;
  buttonClassName?: string;
  disabled?: boolean;
  onClick?: () => void;
  /** any element that is a child of an element with one of these classnames will not trigger closeDropdown */
  excludeClickOutside?: string[];
}

interface State {
  isOpen: boolean;
}

export class DropdownButton extends React.Component<Props, State> {
  private ddButtonRef: HTMLDivElement;
  private ddWrapRef: HTMLDivElement;

  public constructor(props: Props) {
    super(props);

    this.state = {
      isOpen: false,
    };
  }

  private handleWindowClick = (e: MouseEvent) => {
    const { excludeClickOutside } = this.props;

    // dd-wrap is positioned absolutely so isn't included in root elements bounding rect
    const insideButton = isClickInside(e, this.ddButtonRef.getBoundingClientRect());
    const insideDDWrap = this.ddWrapRef && isClickInside(e, this.ddWrapRef.getBoundingClientRect());

    const isInExcluded = excludeClickOutside?.length > 0 && excludeClickOutside.every((className) => {
      const ele = document.getElementsByClassName(className)[0];

      if (ele == null) {
        return;
      }

      return isClickInside(e, ele.getBoundingClientRect());
    });

    if (isInExcluded) {
      return;
    }

    if (!insideDDWrap && !insideButton) {
      this.closeDropdown();
    }
  }

  private openDropdown = () => {
    const { onClick } = this.props;

    if (onClick) {
      onClick();
    }

    this.setState({ isOpen: true }, () => {
      // #550 add window click handler *after* rerender to avoid button click triggering window click in IE11
      requestAnimationFrame(() => window.addEventListener("click", this.handleWindowClick));
    });
  }

  private closeDropdown = () => {
    window.removeEventListener("click", this.handleWindowClick);
    this.setState({ isOpen: false });
  }

  public componentWillUnmount() {
    window.removeEventListener("click", this.handleWindowClick);
  }

  public render() {
    const { className, disabled, label, buttonClassName } = this.props;
    const icon = this.props.icomoon || Icon.Icomoon.more2;
    const handleClick = this.state.isOpen ? this.closeDropdown : this.openDropdown;

    return (
      <div ref={x => this.ddButtonRef = x} className={classNames(className, "dd-button", { "dd-open": this.state.isOpen })}>
        {this.props.label &&
          <Button className={buttonClassName} leftIcon={icon} disabled={disabled} onClick={handleClick}>{label}</Button>
        }
        {!this.props.label &&
          <IconButton icomoon={icon} disabled={disabled} onClick={handleClick} />
        }

        {this.state.isOpen && (
          <div ref={x => this.ddWrapRef = x} className={classNames("dd-wrap")}>
            {this.props.renderDropdown(() => this.closeDropdown())}
          </div>
        )}
      </div>
    );
  }
}
